import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EncounterAdapter } from 'app/core/adapters/encounter.adapter';
import { Encounter, EncounterAPIResponse, encounterI, EncounterListAPIResponse, EncounterListResult } from 'app/core/interfaces/encounter';

import { environment } from 'app/environments/environment';
import { Observable, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class EncounterService {
  private readonly apiUrl = `${environment.apiUrl}/secretarias-encounter`;

  constructor(
    private http: HttpClient,
    private encounterAdapter: EncounterAdapter,
  ) {}

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('userTokenId');
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
  }

  public create(params: any): Observable<any> {
    return this.http.post(this.apiUrl, params, { headers: this.getHeaders() });
  }

  public createNew(encounter: Encounter): Observable<Encounter> {
    const encounterFHIR = this.encounterAdapter.adaptToAPIFormat(encounter);

    return this.http
      .post<EncounterAPIResponse>(`${environment.apiUrl}/secretarias-encounter`, encounterFHIR)
      .pipe(map((encounter: EncounterAPIResponse) => this.encounterAdapter.adapterToModel(encounter)));
  }

  public update(id: string, params: any) {
    return this.http.put(`${environment.apiUrl}/secretarias-encounter/${id}`, params);
  }

  public updateNew(encounter_id: string, encounter: Encounter): Observable<Encounter> {
    const encounterFHIR = this.encounterAdapter.adaptToAPIFormat(encounter);

    return this.http
      .put<EncounterAPIResponse>(`${environment.apiUrl}/secretarias-encounter/${encounter_id}`, encounterFHIR)
      .pipe(map((encounter: EncounterAPIResponse) => this.encounterAdapter.adapterToModel(encounter)));
  }

  public confirmEncounterConvenio(encounter_id: string, data: any): Observable<Encounter> {
    return this.http
      .post<EncounterAPIResponse>(`${environment.apiUrl}/secretarias-encounter/${encounter_id}/confirm-agreement`, data)
      .pipe(map((encounter: EncounterAPIResponse) => this.encounterAdapter.adapterToModel(encounter)));
  }

  public updateStatus(id: string, status: string): Observable<Encounter> {
    return this.http
      .put<EncounterAPIResponse>(`${environment.apiUrl}/secretarias-encounter/${id}/status`, { status })
      .pipe(map((encounter: EncounterAPIResponse) => this.encounterAdapter.adapterToModel(encounter)));
  }

  public delete(id: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`, { headers: this.getHeaders() });
  }

  public show(id: string): Observable<Encounter> {
    return this.http.get<Encounter>(`${this.apiUrl}/${id}`, { headers: this.getHeaders() });
  }

  public list(params: HttpParams): Observable<any> {
    return this.http
      .get<EncounterAPIResponse[]>(`${environment.apiUrl}/secretarias-encounter`, { params: params })
      .pipe(
        map((encounter: EncounterAPIResponse[]) =>
          encounter.map((encounter: EncounterAPIResponse) => this.encounterAdapter.adapterToModel(encounter)),
        ),
      );
  }

  public listNew(params: HttpParams): Observable<Encounter[]> {
    return this.http
      .get<EncounterAPIResponse[]>(`${environment.apiUrl}/secretarias-encounter/organizations`, { params: params })
      .pipe(
        map((encounter: EncounterAPIResponse[]) =>
          encounter.map((encounter: EncounterAPIResponse) => this.encounterAdapter.adapterToModel(encounter)),
        ),
      );
  }

  public listByOrganization(organization_id: string) {
    return this.http.get(`${environment.apiUrl}/secretarias-encounter/organizations/${organization_id}`);
  }

  public listByPractitioner(practitioner_id: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/practitioners/${practitioner_id}`, { headers: this.getHeaders() });
  }

  public listFinishedEncountersByPractitioner(practitioner_id: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/finished_encounters/practitioner/${practitioner_id}`, { headers: this.getHeaders() });
  }

  public listFinishedEncountersByPatient(patient_id: string) {
    return this.http
      .get<EncounterAPIResponse[]>(`${environment.apiUrl}/secretarias-encounter/finished_encounters/patient/${patient_id}`)
      .pipe(
        map((encounter: EncounterAPIResponse[]) =>
          encounter.map((encounter: EncounterAPIResponse) => this.encounterAdapter.adapterToModel(encounter)),
        ),
      );
  }

  public listBySecretary(secretary_id: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/secretaries/${secretary_id}`, { headers: this.getHeaders() });
  }

  public listByPatient(patient_id: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/patients/${patient_id}`, { headers: this.getHeaders() });
  }

  public listByOrganizationNew(organization_id: string): Observable<Encounter[]> {
    return this.http
      .get<EncounterAPIResponse[]>(`${environment.apiUrl}/secretarias-encounter/organizations/${organization_id}`)
      .pipe(
        map((encounter: EncounterAPIResponse[]) =>
          encounter.map((encounter: EncounterAPIResponse) => this.encounterAdapter.adapterToModel(encounter)),
        ),
      );
  }

  public listByOrganizationNewPaged(organization_id: string, params?: HttpParams): Observable<EncounterListResult> {
    return this.http.get<EncounterListAPIResponse>(`${environment.apiUrl}/secretarias-encounter/organizations/${organization_id}`, { params }).pipe(
      map((encounter: EncounterListAPIResponse) => ({
        count: encounter.Count ?? 0,
        items: (encounter.Items ?? []).map((item) => this.encounterAdapter.adapterToModel(item)),
        lastEvaluatedKey: encounter.LastEvaluatedKey ?? null,
      })),
    );
  }

  public listFinishedPatientIdsByOrganization(organization_id: string): Observable<{ patient_id: string; last_appointment: string }[]> {
    return this.http.get<{ patient_id: string; last_appointment: string }[]>(`${this.apiUrl}/organizations/${organization_id}/finished-patients`);
  }

  public listByPractitionerNew(practitioner_id: string): Observable<Encounter[]> {
    return this.http
      .get<EncounterAPIResponse[]>(`${environment.apiUrl}/secretarias-encounter/practitioners/${practitioner_id}`)
      .pipe(
        map((encounter: EncounterAPIResponse[]) =>
          encounter.map((encounter: EncounterAPIResponse) => this.encounterAdapter.adapterToModel(encounter)),
        ),
      );
  }

  public listByPractitionerNewPaged(practitioner_id: string, params?: HttpParams): Observable<EncounterListResult> {
    return this.http.get<EncounterListAPIResponse>(`${environment.apiUrl}/secretarias-encounter/practitioners/${practitioner_id}`, { params }).pipe(
      map((encounter: EncounterListAPIResponse) => ({
        count: encounter.Count ?? 0,
        items: (encounter.Items ?? []).map((item) => this.encounterAdapter.adapterToModel(item)),
        lastEvaluatedKey: encounter.LastEvaluatedKey ?? null,
      })),
    );
  }

  public listByPractitionerHealthcareservice(practitioner_id: string, healthcareservice: string): Observable<Encounter[]> {
    return this.http
      .get<
        EncounterAPIResponse[]
      >(`${environment.apiUrl}/secretarias-encounter/practitioners/${practitioner_id}/healthcareservice/${healthcareservice}`)
      .pipe(
        map((encounter: EncounterAPIResponse[]) =>
          encounter.map((encounter: EncounterAPIResponse) => this.encounterAdapter.adapterToModel(encounter)),
        ),
      );
  }

  public listBySecretaryNew(secretary_id: string, filters?: any, params?: HttpParams): Observable<EncounterListResult> {
    return this.http.get<EncounterListAPIResponse>(`${environment.apiUrl}/secretarias-encounter/secretaries/${secretary_id}`, { params }).pipe(
      map((encounter: EncounterListAPIResponse) => ({
        count: encounter.Count ?? 0,
        items: (encounter.Items ?? []).map((item) => this.encounterAdapter.adapterToModel(item)),
        lastEvaluatedKey: encounter.LastEvaluatedKey ?? null,
      })),
    );
  }

  public listByPatientNew(patient_id: string) {
    return this.http
      .get<EncounterAPIResponse[]>(`${environment.apiUrl}/secretarias-encounter/patients/${patient_id}`)
      .pipe(
        map((encounter: EncounterAPIResponse[]) =>
          encounter.map((encounter: EncounterAPIResponse) => this.encounterAdapter.adapterToModel(encounter)),
        ),
      );
  }

  public addDocument(encounter_id: string, files: any) {
    const contentData = this.encounterAdapter.adaptAddDocument(files);

    return this.http.post<EncounterAPIResponse>(`${environment.apiUrl}/secretarias-encounter/${encounter_id}/document`, contentData);
  }

  public removeDocument(encounter_id: string, data: any) {
    return this.http.delete<any>(`${environment.apiUrl}/secretarias-encounter/${encounter_id}/document`, data);
  }
}
